import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import useFetchObjects from "../../api/useFetchObjects";
import useAdd from "../../api/useAdd";
import useUpdate from "../../api/useUpdate";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const EntityDialog = ({ open, title, fields, onClose, onSubmit, submitLabel }) => {
  const [formState, setFormState] = useState({});

  useEffect(() => {
    if (open) {
      const initial = {};
      fields.forEach((field) => {
        initial[field.name] = field.value ?? "";
      });
      setFormState(initial);
    }
  }, [open, fields]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
          {fields.map((field) =>
            <TextField
              key={field.name}
              label={field.label}
              value={formState[field.name] ?? ""}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, [field.name]: e.target.value }))
              }
              fullWidth
              required={field.required}
              multiline={field.multiline}
              rows={field.rows}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(formState)}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LocationTable = ({
  title,
  addLabel,
  rows,
  columns,
  onAdd,
  onEdit,
  onDelete,
  emptyText,
  searchValue = "",
  onSearchChange = () => {},
  searchPlaceholder = "",
}) => {
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">{title}</Typography>
        <Button variant="contained" onClick={onAdd}>
          {addLabel}
        </Button>
      </Box>
      {searchPlaceholder ? (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            value={searchValue}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        </Box>
      ) : null}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row.id || index} hover>
                  {row.cells.map((cell, cellIndex) => (
                    <TableCell key={`${row.id || index}-${cellIndex}`}>{cell}</TableCell>
                  ))}
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onEdit(row.raw)}
                      sx={{ mr: 1, minWidth: 64 }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => onDelete(row.raw)}
                      sx={{ minWidth: 72 }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const LocationSettings = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [tab, setTab] = useState(0);
  const [countrySearch, setCountrySearch] = useState("");
  const [provinceSearch, setProvinceSearch] = useState("");

  const countryQuery = useFetchObjects(
    ["countries"],
    "shop/countries/?page_size=all",
    token
  );
  const provinceQuery = useFetchObjects(
    ["provinces"],
    "shop/provinces/?page_size=all",
    token
  );

  const countries = useMemo(
    () => (Array.isArray(countryQuery.data) ? countryQuery.data : countryQuery.data?.results || []),
    [countryQuery.data]
  );
  const provinces = useMemo(
    () => (Array.isArray(provinceQuery.data) ? provinceQuery.data : provinceQuery.data?.results || []),
    [provinceQuery.data]
  );

  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [provinceDialogOpen, setProvinceDialogOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editingProvince, setEditingProvince] = useState(null);
  const [countryRefreshToken, setCountryRefreshToken] = useState(0);
  const [provinceRefreshToken, setProvinceRefreshToken] = useState(0);
  const handledCountryAddRef = useRef(false);
  const handledProvinceAddRef = useRef(false);
  const handledCountryUpdateRef = useRef(false);
  const handledProvinceUpdateRef = useRef(false);

  const countryAdd = useAdd("shop/countries", token);
  const provinceAdd = useAdd("shop/provinces", token);
  const countryUpdate = useUpdate("shop/countries", token);
  const provinceUpdate = useUpdate("shop/provinces", token);
  const { handleDelete: deleteCountry, ConfirmDialog: CountryConfirmDialog } = useDelete("shop/countries", token);
  const { handleDelete: deleteProvince, ConfirmDialog: ProvinceConfirmDialog } = useDelete("shop/provinces", token);

  const countryRows = countries.map((country) => ({
    raw: country,
    cells: [
      country.name,
      country.note || "-",
      new Date(country.created_at || Date.now()).toLocaleDateString(),
    ],
  }));

  const filteredCountryRows = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return countryRows;
    return countryRows.filter((row) => {
      const name = String(row.raw?.name || "").toLowerCase();
      const note = String(row.raw?.note || "").toLowerCase();
      return name.includes(query) || note.includes(query);
    });
  }, [countryRows, countrySearch]);

  const provinceRows = provinces.map((province) => ({
    raw: province,
    cells: [
      province.name,
      province.note || "-",
      new Date(province.created_at || Date.now()).toLocaleDateString(),
    ],
  }));

  const filteredProvinceRows = useMemo(() => {
    const query = provinceSearch.trim().toLowerCase();
    if (!query) return provinceRows;
    return provinceRows.filter((row) => {
      const name = String(row.raw?.name || "").toLowerCase();
      const note = String(row.raw?.note || "").toLowerCase();
      return name.includes(query) || note.includes(query);
    });
  }, [provinceRows, provinceSearch]);

  const handleCountrySubmit = (values) => {
    const formData = new FormData();
    formData.append("name", values.name || "");
    formData.append("note", values.note || "");
    if (editingCountry) {
      countryUpdate.handleUpdate(editingCountry.id, formData);
    } else {
      countryAdd.handleAdd(formData);
    }
  };

  const handleProvinceSubmit = (values) => {
    const formData = new FormData();
    formData.append("name", values.name || "");
    formData.append("note", values.note || "");
    if (editingProvince) {
      provinceUpdate.handleUpdate(editingProvince.id, formData);
    } else {
      provinceAdd.handleAdd(formData);
    }
  };

  useEffect(() => {
    if (countryAdd.resetForm && !handledCountryAddRef.current) {
      handledCountryAddRef.current = true;
      setCountryDialogOpen(false);
      setEditingCountry(null);
      setCountryRefreshToken((value) => value + 1);
    } else if (!countryAdd.resetForm) {
      handledCountryAddRef.current = false;
    }
  }, [countryAdd.resetForm]);

  useEffect(() => {
    if (countryUpdate.isSuccess && !handledCountryUpdateRef.current) {
      handledCountryUpdateRef.current = true;
      setCountryDialogOpen(false);
      setEditingCountry(null);
      setCountryRefreshToken((value) => value + 1);
    } else if (!countryUpdate.isSuccess) {
      handledCountryUpdateRef.current = false;
    }
  }, [countryUpdate.isSuccess]);

  useEffect(() => {
    if (provinceAdd.resetForm && !handledProvinceAddRef.current) {
      handledProvinceAddRef.current = true;
      setProvinceDialogOpen(false);
      setEditingProvince(null);
      setProvinceRefreshToken((value) => value + 1);
    } else if (!provinceAdd.resetForm) {
      handledProvinceAddRef.current = false;
    }
  }, [provinceAdd.resetForm]);

  useEffect(() => {
    if (provinceUpdate.isSuccess && !handledProvinceUpdateRef.current) {
      handledProvinceUpdateRef.current = true;
      setProvinceDialogOpen(false);
      setEditingProvince(null);
      setProvinceRefreshToken((value) => value + 1);
    } else if (!provinceUpdate.isSuccess) {
      handledProvinceUpdateRef.current = false;
    }
  }, [provinceUpdate.isSuccess]);

  useEffect(() => {
    if (countryRefreshToken > 0) {
      countryQuery.refetch();
    }
  }, [countryRefreshToken, countryQuery.refetch]);

  useEffect(() => {
    if (provinceRefreshToken > 0) {
      provinceQuery.refetch();
    }
  }, [provinceRefreshToken, provinceQuery.refetch]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t("sidebar.locationSettings", "Location Settings")}
      </Typography>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab label={t("locationSettings.countries", "Countries")} />
        <Tab label={t("locationSettings.provinces", "Provinces")} />
      </Tabs>

      {tab === 0 && (
        <Box sx={{ mt: 3 }}>
          <LocationTable
            title={t("locationSettings.countries", "Countries")}
            addLabel={t("locationSettings.addCountry", "Add Country")}
            rows={filteredCountryRows}
            columns={[
              t("locationSettings.countryName", "Country Name"),
              t("locationSettings.note", "Note"),
              t("locationSettings.createdAt", "Created At"),
              t("locationSettings.action", "Action"),
            ]}
            emptyText={t("locationSettings.noCountries", "No countries found.")}
            searchValue={countrySearch}
            onSearchChange={(e) => setCountrySearch(e.target.value)}
            searchPlaceholder={t("locationSettings.searchCountries", "Search Countries")}
            onAdd={() => {
              setEditingCountry(null);
              setCountryDialogOpen(true);
            }}
            onEdit={(country) => {
              setEditingCountry(country);
              setCountryDialogOpen(true);
            }}
            onDelete={(country) => deleteCountry(country.id)}
          />
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ mt: 3 }}>
          <LocationTable
            title={t("locationSettings.provinces", "Provinces")}
            addLabel={t("locationSettings.addProvince", "Add Province")}
            rows={filteredProvinceRows}
            columns={[
              t("locationSettings.provinceName", "Province Name"),
              t("locationSettings.note", "Note"),
              t("locationSettings.createdAt", "Created At"),
              t("locationSettings.action", "Action"),
            ]}
            emptyText={t("locationSettings.noProvinces", "No provinces found.")}
            searchValue={provinceSearch}
            onSearchChange={(e) => setProvinceSearch(e.target.value)}
            searchPlaceholder={t("locationSettings.searchProvinces", "Search Provinces")}
            onAdd={() => {
              setEditingProvince(null);
              setProvinceDialogOpen(true);
            }}
            onEdit={(province) => {
              setEditingProvince(province);
              setProvinceDialogOpen(true);
            }}
            onDelete={(province) => deleteProvince(province.id)}
          />
        </Box>
      )}

      <EntityDialog
        open={countryDialogOpen}
        title={editingCountry ? t("locationSettings.editCountry", "Edit Country") : t("locationSettings.addCountry", "Add Country")}
        submitLabel={editingCountry ? t("locationSettings.update", "Update") : t("locationSettings.save", "Save")}
        onClose={() => {
          setCountryDialogOpen(false);
          setEditingCountry(null);
        }}
        onSubmit={handleCountrySubmit}
        fields={[
          {
            name: "name",
            label: t("locationSettings.countryName", "Country Name"),
            value: editingCountry?.name || "",
            required: true,
          },
          {
            name: "note",
            label: t("locationSettings.note", "Note"),
            value: editingCountry?.note || "",
            multiline: true,
            rows: 4,
          },
        ]}
      />

      <EntityDialog
        open={provinceDialogOpen}
        title={editingProvince ? t("locationSettings.editProvince", "Edit Province") : t("locationSettings.addProvince", "Add Province")}
        submitLabel={editingProvince ? t("locationSettings.update", "Update") : t("locationSettings.save", "Save")}
        onClose={() => {
          setProvinceDialogOpen(false);
          setEditingProvince(null);
        }}
        onSubmit={handleProvinceSubmit}
        fields={[
          {
            name: "name",
            label: t("locationSettings.provinceName", "Province Name"),
            value: editingProvince?.name || "",
            required: true,
          },
          {
            name: "note",
            label: t("locationSettings.note", "Note"),
            value: editingProvince?.note || "",
            multiline: true,
            rows: 4,
          },
        ]}
      />
      <CountryConfirmDialog />
      <ProvinceConfirmDialog />
    </Box>
  );
};

export default LocationSettings;
